import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { FieldArray, getIn } from 'formik';
import {
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Tooltip,
    Typography,
    FormHelperText,
    Box,
    Switch,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Input,
    InputAdornment
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { FeeScheduleSelection } from '../../feeSchedulePage/feeScheduleSelectionComponent';
import { DetailsPopover } from '../../../common/detailsPopover';
import { PAY_METHOD_PER_DIEM_STOP_LOSS, PAY_METHOD_PER_UNIT, PAY_METHOD_PERCENTAGE, PAY_METHOD_STOP_LOSS, RULE_APPLY_AT_DATE_OF_SERVICE } from '../../../store/constants/contract';
import GlobalFeeScheduleFields from './GlobalFeeScheduleFields';
import { checkRequiresGlobalConfig } from '../../../store/api/globalFeeSchedule';
import { DateRangePicker } from '../../../common/dateRangeControl';
import { useStatePtr } from '@pure-ptr/react';
import { getLast30Days } from '../../../util/date';
import { PopoverLink } from '../../../common';

// Simple date formatter
const formatDate = (dateString: string | Date, format?: string): string => {
    if (!dateString) return '';
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;

    if (format === 'YYYY-MM-DD') {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    // Default format: M/D/YYYY
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
};

interface PaymentFeeScheduleEntry {
    // Local Fee Schedule fields
    feeScheduleId: number | null;
    feeScheduleName: string;
    feescheduleLabelToRateXRefId: number | null;
    // Global Fee Schedule fields
    globalFeeScheduleId?: number | null;
    globalFeescheduleLabelToRateXRefId?: number | null;
    globalFeeScheduleGPCI?: string | null;
    globalFeeScheduleState?: string | null;
    globalFeeScheduleLocality?: string | null;
    // Common fields
    payMethodId: number | null;
    payValue: number | null;
    dosMaxRate?: number | null;
    avgLengthOfStay: number | null;
    payValuePercentage: number | null;
    effectiveDate: string | null,
    expirationDate: string | null,
    rateLabels?: any[]; // Store rate labels per entry
    feeSchedule?: any; // Store fee schedule object to access dates
    // UI-only Global Fee Schedule Configuration fields
    requiresGlobalConfig?: boolean;
    selectedRateType?: string | null;
    selectedState?: string | null;
    selectedLocality?: string | null;
    selectedGpci?: string | null;
    global_schedule_rate_column?: string | null;
    dateRange: any,
    //Anesthesia
    useMinutesPerUnit?: boolean;
    minutesPerUnit?: number | null;
}

interface RulePaymentFeeScheduleArrayProps {
    values: any;
    setFieldValue: (field: string, value: any) => void;
    errors: any;
    touched: any;
    handleChange: (e: any) => void;
    handleBlur: (e: any) => void;
    payMethods: any[];
    classes: any,
    isPayMethodPerDiemStopLoss: (payMethodId: any) => boolean;
    isPayMethodAnesthesia: (payMethodId: any) => boolean;
    areAllPayMethodsPerUnit: (values: any) => boolean;
}

const RulePaymentFeeScheduleArray: React.FC<RulePaymentFeeScheduleArrayProps> = ({
    values,
    setFieldValue,
    errors,
    touched,
    handleChange,
    handleBlur,
    payMethods,
    classes,
    isPayMethodPerDiemStopLoss,
    isPayMethodAnesthesia,
    areAllPayMethodsPerUnit
}) => {
    const entries = values.rulePaymentFeeScheduleXRef || [];

    const getHelperText = (index, name) => {
        return (
            getIn(touched, `rulePaymentFeeScheduleXRef.${index}.${name}`) &&
            getIn(errors, `rulePaymentFeeScheduleXRef.${index}.${name}`)
        );
    };

    const hasError = (index, name) => {
        return Boolean(
            getIn(touched, `rulePaymentFeeScheduleXRef.${index}.${name}`) &&
            getIn(errors, `rulePaymentFeeScheduleXRef.${index}.${name}`)
        );
    };

    const makeFormikPtr = (
        value: any,
        setFieldValue: (field: string, value: any) => void,
        name: string
    ) => ({
        value,
        set: (v: any) => setFieldValue(name, v),
    });

    const getDefaultDateRange = () => {
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);

        return {
            startdate: today.toISOString().slice(0, 10),
            enddate: tomorrow.toISOString().slice(0, 10),
            relativeFilter: 'custom',
        };
    };

    function checkPayMethodsPerUnit(payMethodId, values) {
        let isPayMethodUnit = false;
        if (payMethods && payMethods.length > 0) {
            const payMethod = payMethods.find(item => item.id === payMethodId);
            isPayMethodUnit =  payMethod && payMethod.name === PAY_METHOD_PER_UNIT;
            if (payMethod.name === PAY_METHOD_PER_DIEM_STOP_LOSS || payMethod.name === PAY_METHOD_STOP_LOSS) {
                setFieldValue('applyAt', 'Claim');
            }
        }
        if(isPayMethodUnit && areAllPayMethodsPerUnit(values)) {
            return true;
        }
        return false;
    }
    
    return (
        <FieldArray
            name="rulePaymentFeeScheduleXRef"
            render={({ remove, push }) => (
                <Grid container spacing={2}>
                    {entries.map((entry: PaymentFeeScheduleEntry, index: number) => {
                        const fieldName = `rulePaymentFeeScheduleXRef[${index}]`;
                        const fieldErrors = errors?.rulePaymentFeeScheduleXRef?.[index];
                        const fieldTouched = touched?.rulePaymentFeeScheduleXRef?.[index];
                        const dateRangePtr = makeFormikPtr(
                            entry.dateRange,
                            setFieldValue,
                            `${fieldName}.dateRange`
                        );

                        return (
                            <Grid item xs={12} key={index}>
                                <Grid container spacing={1} alignItems="center">
                                    {/* Multiple payments for paySource charge */}
                                    <Grid item xs={3}>
                                        <FormControl
                                            className={classes.formControl}
                                        >
                                            <InputLabel htmlFor={`${fieldName}.payMethodId`}>
                                                Pay Method
                                            </InputLabel>
                                            <Select
                                                required
                                                value={entry.payMethodId}
                                                onChange={(e) => {
                                                    setFieldValue(`${fieldName}.payMethodId`, e.target.value);
                                                    if(!checkPayMethodsPerUnit(e.target.value, values)) {
                                                        setFieldValue(`useBaseRate`, false);
                                                    }
                                                }}
                                                error={hasError(index, 'payMethodId')}
                                                input={<Input id={`${fieldName}.payMethodId`} />}
                                                inputProps={{
                                                    name: `${fieldName}.payMethodId`,
                                                    id: `${fieldName}.payMethodId`,
                                                }}
                                            >
                                                {payMethods.map((payMethod) => (
                                                    <MenuItem
                                                        key={payMethod.id}
                                                        value={payMethod.id}
                                                    >
                                                        {payMethod.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            <FormHelperText error sx={{whiteSpace:"pre-wrap", wordWrap: "break-word", maxWidth: "100%"}}>
                                                {getHelperText(index, "payMethodId") || ' '}
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={3}>
                                        <FormControl
                                            className={classes.formControl}
                                        >
                                            <DateRangePicker
                                                disabled={false}
                                                valuePtr={dateRangePtr} 
                                                label="Date Range"
                                                enabledSwitch={false} 
                                                disableDateSection={false}
                                                disableRelativeFilter={true}
                                            />
                                            <FormHelperText error sx={{whiteSpace:"pre-wrap", wordWrap: "break-word", maxWidth: "100%"}}>
                                                {getHelperText(index, "dateRange") || ' '}
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>

                                    {/* Pay Value - Show when NOT split payment */}
                                    <Grid item xs={(isPayMethodPerDiemStopLoss && isPayMethodPerDiemStopLoss(entry.payMethodId)) ? 1 : 2}>
                                            <TextField
                                                required
                                                type="number"
                                                label="Pay Value"
                                                name={`${fieldName}.payValue`}
                                                className={classes.textField}
                                                margin="normal"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={entry.payValue}
                                                helperText={getHelperText(index, "payValue") || ' '}
                                                error={hasError(index, 'payValue')}
                                            />
                                    </Grid>
                                    {values.applyAt === RULE_APPLY_AT_DATE_OF_SERVICE && (
                                        <Grid item xs={2}>
                                            <TextField
                                                size="small"
                                                label="DOS Max Rate"
                                                name={`${fieldName}.dosMaxRate`}
                                                type="number"
                                                className={classes.textField}
                                                margin="normal"
                                                value={entry.dosMaxRate}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                helperText={getHelperText(index, "dosMaxRate") || ' '}
                                                error={hasError(index, 'dosMaxRate')}
                                            />
                                        </Grid>
                                    )}
                                    {isPayMethodAnesthesia(entry.payMethodId) && (
                                        <>
                                            <Grid item xs={2}>
                                                <Box display="flex" alignItems="center">
                                                    <Typography
                                                        sx={{
                                                            fontFamily: 'Poppins',
                                                            fontWeight: 400,
                                                            fontSize: '0.875rem',
                                                            color: !entry.useMinutesPerUnit ? 'primary.main' : 'text.secondary',
                                                            marginRight: 1
                                                        }}
                                                    >
                                                        Based on units
                                                    </Typography>
                                                    <Switch
                                                        checked={entry.useMinutesPerUnit || false}
                                                        onChange={(e) => {
                                                            setFieldValue(`${fieldName}.useMinutesPerUnit`, e.target.checked);

                                                            if (e.target.checked && !entry.minutesPerUnit) {
                                                                setFieldValue(`${fieldName}.minutesPerUnit`, 15);
                                                            }
                                                        }}
                                                        color="primary"
                                                        size="small"
                                                    />
                                                   
                                                    <Typography
                                                        sx={{
                                                            fontFamily: 'Poppins',
                                                            fontWeight: 400,
                                                            fontSize: '0.875rem',
                                                            color: entry.useMinutesPerUnit ? 'primary.main' : 'text.secondary',
                                                            marginLeft: 1
                                                        }}
                                                    >
                                                        Based on minutes
                                                    </Typography>
                                                </Box>
                                            </Grid>

                                            {/* Minutes Per Unit Field - Show when useMinutesPerUnit is true */}
                                            {entry.useMinutesPerUnit && (
                                                <Grid item xs={1}>
                                                    <TextField
                                                        required
                                                        type="number"
                                                        label="Minutes Per Unit"
                                                        name={`${fieldName}.minutesPerUnit`}
                                                        className={classes.textField}
                                                        margin="normal"
                                                        onBlur={handleBlur}
                                                        onChange={handleChange}
                                                        value={entry.minutesPerUnit}
                                                        helperText={getHelperText(index, "minutesPerUnit") || ' '}
                                                        error={hasError(index, "minutesPerUnit")}
                                                        inputProps={{ min: 1 }}
                                                        InputProps={{
                                                            endAdornment: <InputAdornment position="end">
                                                                <PopoverLink
                                                                    maxWidth={600}
                                                                    columnData={
                                                                        <i className="fas fa-question-circle text-info" style={{ cursor: 'pointer', marginLeft: 4 }}></i>
                                                                    }
                                                                    hoverData={[
                                                                        {
                                                                            label: 'Formula',
                                                                            data: '(Base Units + Time Units (Charge Units / Minutes Per Units)) x Conversion Factor (Pay Value)'
                                                                        },
                                                                        {
                                                                            label: '',
                                                                            data: '',
                                                                            type: 'SPACE'
                                                                        },
                                                                        {
                                                                            label: 'Description',
                                                                            data: 'Choose whether anesthesia values come from units or minutes on the claim. If minutes are selected, enter the conversion value (default is 15). Selecting units will not change current behavior.'
                                                                        }
                                                                    ]}
                                                                />
                                                            </InputAdornment>
                                                        }}
                                                    />
                                                </Grid>
                                            )}
                                        </>
                                    )}

                                    {isPayMethodPerDiemStopLoss && isPayMethodPerDiemStopLoss(entry.payMethodId) && (
                                        <Grid item xs={2}>
                                            <TextField
                                                type="number"
                                                label="Pay Percentage"
                                                name={`${fieldName}.payValuePercentage`}
                                                className={classes.textField}
                                                margin="normal"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={entry.payValuePercentage}
                                                helperText={getHelperText(index, "payValuePercentage") || ' '}
                                                error={hasError(index, 'payValuePercentage')}
                                            />
                                        </Grid>
                                    )}

                                    {isPayMethodPerDiemStopLoss && isPayMethodPerDiemStopLoss(entry.payMethodId) && !values.isAlosEnabled && (
                                        <Grid item xs={2}>
                                            <TextField
                                                required
                                                type="number"
                                                label="Avg Length of Stay"
                                                name={`${fieldName}.avgLengthOfStay`}
                                                className={classes.textField}
                                                margin="normal"
                                                onBlur={handleBlur}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (value === '') {
                                                        handleChange(e);
                                                        return;
                                                    }
                                                    const decimalPart = value.split('.')[1];
                                                    if (!decimalPart || decimalPart.length <= 1) {
                                                        handleChange(e);
                                                    }
                                                }}
                                                value={entry.avgLengthOfStay}
                                                helperText={getHelperText(index, "avgLengthOfStay") || ' '}
                                                error={hasError(index, 'avgLengthOfStay')}
                                                inputProps={{ step: "0.1" }}
                                            />
                                        </Grid>
                                    )}
                                    {/* Icons - minimal space */}
                                    <Grid item xs={1}>
                                        <Box display="flex" gap={0.5}>
                                            {index === 0 && (
                                                <Tooltip title="Add Payment">
                                                    <span>
                                                        <IconButton
                                                            color="primary"
                                                            aria-label="Add"
                                                            disabled={!entry.payMethodId} // Disable if no type selected
                                                            onClick={() =>
                                                                push({
                                                                    feeScheduleId: null,
                                                                    feeScheduleName: '',
                                                                    feescheduleLabelToRateXRefId: null,
                                                                    payMethodId: null,
                                                                    payValue: null,
                                                                    dosMaxRate: null,
                                                                    avgLengthOfStay: null,
                                                                    payValuePercentage: null,
                                                                    effectiveDate: null,
                                                                    expirationDate: null,
                                                                    useMinutesPerUnit: false,
                                                                    minutesPerUnit: 15,
                                                                    rateLabels: [],
                                                                    feeSchedule: null,
                                                                    // Global config fields
                                                                    requiresGlobalConfig: false,
                                                                    selectedRateType: null,
                                                                    selectedState: null,
                                                                    selectedLocality: null,
                                                                    selectedGpci: null,
                                                                    global_schedule_rate_column: null,
                                                                    dateRange: getDefaultDateRange()
                                                                })
                                                            }
                                                        >
                                                            <AddCircleIcon />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                            {entries.length > 1 && (
                                                <Tooltip title="Delete Payment">
                                                    <IconButton
                                                        aria-label="Delete"
                                                        onClick={() => {
                                                            remove(index);
                                                        }}
                                                        size="small"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Grid>
                        );
                    })}
                </Grid>
            )}
        />
    );
};

export default RulePaymentFeeScheduleArray;
